import React,{useCallback,useEffect,useState} from "react";
import {api} from "../api";

// The Editorial Calendar is the single publishing authority for new Daily Words.
// Legacy Daily Words remain read-only as a fallback until an approved entry is due.
// Writer/Book Spotlight and Mystery250 answers remain independently managed.
const utcToday=()=>new Date().toISOString().slice(0,10);
function nextMonday(){
 const now=new Date(utcToday()+"T00:00:00.000Z");
 now.setUTCDate(now.getUTCDate()+((8-now.getUTCDay())%7||7));
 return now.toISOString().slice(0,10);
}
// Move to the next unscheduled date after approval, without overwriting another entry.
function nextAvailableDate(item,entries){
 const date=new Date((item.dateKey>utcToday()?item.dateKey:utcToday())+"T00:00:00.000Z");
 const occupied=new Set(entries.filter(x=>x.kind===item.kind).map(x=>x.dateKey));
 for(let attempt=0;attempt<3660;attempt++){
  const days=item.kind==="weeklyMystery"?((8-date.getUTCDay())%7||7):1;
  date.setUTCDate(date.getUTCDate()+days);
  const next=date.toISOString().slice(0,10);
  if(!occupied.has(next))return next;
 }
 return date.toISOString().slice(0,10);
}
const blankDaily=()=>({sourceType:"original",quote:"",quoteFr:"",author:"NextScenes",
 workTitle:"",sourceReference:"",sourceUrl:"",reflection:"",reflectionFr:"",
 translationCredit:"",rightsBasis:"original",rightsVerified:false});
const blankWeekly=()=>({mysteryId:"",title:"",titleFr:"",teaser:"",teaserFr:"",cta:"",ctaFr:""});
const fieldStyle={display:"grid",gap:5,fontWeight:600,fontSize:14,minWidth:0};
const inputStyle={width:"100%",border:"1px solid #cbded2",borderRadius:9,padding:"10px 12px",fontSize:14,background:"white",color:"#173b2f"};
const btn={border:"1px solid #b9d6c4",borderRadius:20,padding:"9px 13px",fontWeight:700,background:"white",color:"#13623b",cursor:"pointer"};
function Field({label,value,onChange,large=false,disabled=false,type="text"}){
 return <label style={fieldStyle}>{label}
  {large?<textarea rows={3} value={value||""} onChange={e=>onChange(e.target.value)} disabled={disabled} style={{...inputStyle,resize:"vertical"}}/>:
  <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} disabled={disabled} style={inputStyle}/>}
 </label>;
}
export default function EditorialScheduleV1({lang="en"}){
 const fr=lang==="fr";
 const [kind,setKind]=useState("dailyWisdom");
 const [dateKey,setDateKey]=useState(utcToday());
 const [daily,setDaily]=useState(blankDaily);
 const [weekly,setWeekly]=useState(blankWeekly);
 const [items,setItems]=useState([]);
 const [mysteries,setMysteries]=useState([]);
 const [selectedId,setSelectedId]=useState("");
 const [busy,setBusy]=useState(false),[error,setError]=useState(""),[notice,setNotice]=useState("");
 const refresh=useCallback(async()=>{
  const result=await api("/api/home/editorial-schedule",{method:"GET"});
  const loaded=Array.isArray(result?.items)?result.items:[];
  setItems(loaded);
  return loaded;
 },[]);
 useEffect(()=>{
  let active=true;
  refresh().catch(e=>{if(active)setError(e?.data?.message||e?.message||"Could not load editorial calendar.");});
  api("/api/admin/public-homepage/mysteries?limit=100",{method:"GET"})
    .then(r=>{if(active)setMysteries(r?.mysteries||[]);})
    .catch(()=>{if(active)setMysteries([]);});
  return()=>{active=false;};
 },[refresh]);
 const selectKind=value=>{
  setKind(value);setSelectedId("");setDateKey(value==="weeklyMystery"?nextMonday():utcToday());
  setDaily(blankDaily());setWeekly(blankWeekly());setError("");setNotice("");
 };
 const choose=item=>{
  setKind(item.kind);setDateKey(item.dateKey);setSelectedId(String(item._id));
  setDaily({...blankDaily(),...(item.daily||{})});
  setWeekly({...blankWeekly(),...(item.weekly||{}),
    mysteryId:typeof item.weekly?.mysteryId==="object"?
      String(item.weekly.mysteryId?._id||""):String(item.weekly?.mysteryId||"")});
  setError("");setNotice("");
  window.scrollTo({top:0,behavior:"smooth"});
 };
 const changeDaily=(key,value)=>setDaily(x=>({...x,[key]:value}));
 const changeWeekly=(key,value)=>setWeekly(x=>({...x,[key]:value}));
 const save=async()=>{
  setBusy(true);setError("");setNotice("");
  try{
   const saved=await api("/api/home/editorial-schedule/"+kind+"/"+dateKey,{
    method:"PUT",body:JSON.stringify(kind==="dailyWisdom"?{daily}:{weekly})
   });
   if(!saved?.ok)throw Error("Could not save draft.");
   setSelectedId(String(saved.item._id));
   await refresh();
   setNotice(fr?"Brouillon enregistré. Pas encore publié.":"Draft saved. Not published.");
  }catch(e){setError(e?.data?.message||e?.message||"Save failed.");}
  finally{setBusy(false);}
 };
 const changeApproval=async(item,action)=>{
  setBusy(true);setError("");setNotice("");
  try{
   const response=await api("/api/home/editorial-schedule/"+item._id+"/"+action,{
    method:"POST",body:JSON.stringify({})
   });
   if(!response?.ok)throw Error("Action failed.");
   const loaded=await refresh();
   // Only clear the form when its own entry was approved successfully.
   // Do not discard unrelated edits when approving another queue item.
   if(action==="approve"&&String(item._id)===selectedId){
    setSelectedId("");
    setDaily(blankDaily());
    setWeekly(blankWeekly());
    setDateKey(nextAvailableDate(item,loaded));
   }
   setNotice(response.message||"Editorial calendar updated.");
  }catch(e){setError(e?.data?.message||e?.message||"Calendar update failed.");}
  finally{setBusy(false);}
 };
 const list=items.filter(x=>x.kind===kind);
 const selected=list.find(x=>String(x._id)===selectedId);
 const isLiveApproved=selected?.status==="approved" && selected.dateKey<=utcToday();
 const approvedFuture=list.filter(x=>x.status==="approved"&&x.dateKey>=utcToday()).length;
 const selectMystery=value=>{
  const m=mysteries.find(x=>x.id===value);
  setWeekly(w=>({...w,mysteryId:value,title:w.title||m?.title||"",teaser:w.teaser||m?.teaser||""}));
 };
 return <div style={{display:"grid",gap:16}}>
  <div className="ns-card" style={{padding:18}}>
   <h2 style={{margin:"0 0 8px",fontSize:20}}>{fr?"Calendrier éditorial":"Editorial Calendar"}</h2>
   <p style={{margin:"0 0 12px",fontSize:14,color:"#567064"}}>
    {fr?"Préparez les textes dans les deux langues, vérifiez les sources, puis approuvez la date de publication.":
      "Prepare both languages, verify sources and rights, then approve a publication date. Only approved entries can appear automatically."}
   </p>
   <div style={{display:"flex",gap:9,flexWrap:"wrap"}}>
    <button type="button" style={{...btn,...(kind==="dailyWisdom"?{background:"#116f43",color:"white"}:{})}}
      onClick={()=>selectKind("dailyWisdom")}>{fr?"Pensées quotidiennes":"Daily Words"}</button>
    <button type="button" style={{...btn,...(kind==="weeklyMystery"?{background:"#116f43",color:"white"}:{})}}
      onClick={()=>selectKind("weeklyMystery")}>Mystery250 · {fr?"Hebdomadaire":"Weekly"}</button>
   </div>
   <p style={{fontSize:12,color:"#567064",margin:"12px 0 0"}}>
    {fr?"Heure de rotation : 00:00 UTC (Bamako). Pas de générateur automatique de citations.":
      "Rotation: 00:00 UTC (Bamako). No automatically generated or unverified quotations."}
   </p>
  </div>
  {error?<p role="alert" style={{color:"#a12b2b",fontWeight:600}}>{error}</p>:null}
  {notice?<p role="status" style={{color:"#16613c",fontWeight:600}}>{notice}</p>:null}
  <section className="ns-card" style={{padding:18}}>
   <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
    <h3 style={{margin:0,fontSize:18}}>{selectedId?(fr?"Modifier le contenu":"Edit scheduled entry"):(fr?"Nouvelle entrée":"New entry")}</h3>
    <button type="button" style={btn} onClick={()=>selectKind(kind)}>{fr?"Nouvelle entrée":"Clear / New"}</button>
   </div>
   <div style={{margin:"15px 0",maxWidth:320}}>
    <Field label={kind==="dailyWisdom"?(fr?"Date de publication (UTC)":"Publication date (UTC)"):(fr?"Lundi de publication (UTC)":"Publication Monday (UTC)")}
      type="date" disabled={Boolean(selectedId)} value={dateKey} onChange={setDateKey}/>
   </div>
   {kind==="dailyWisdom"?<div style={{display:"grid",gap:12}}>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
     <label style={fieldStyle}>{fr?"Type de contenu":"Content type"}
      <select value={daily.sourceType} onChange={e=>{
        const quotation=e.target.value==="quotation";
        setDaily(d=>({...d,sourceType:quotation?"quotation":"original",rightsBasis:quotation?"public_domain":"original",rightsVerified:false,
          author:quotation?"":d.author||"NextScenes"}));
      }} style={inputStyle}>
       <option value="original">{fr?"Réflexion originale":"Original NextScenes reflection"}</option>
       <option value="quotation">{fr?"Citation attribuée":"Attributed literary/philosophical quotation"}</option>
      </select>
     </label>
     <Field label={fr?"Auteur / attribution":"Author / attribution"} value={daily.author} onChange={v=>changeDaily("author",v)}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(270px,1fr))",gap:12}}>
     <Field large label={fr?"Texte anglais":"English text"} value={daily.quote} onChange={v=>changeDaily("quote",v)}/>
     <Field large label={fr?"Texte français":"French text"} value={daily.quoteFr} onChange={v=>changeDaily("quoteFr",v)}/>
    </div>
    {daily.sourceType==="quotation"?<>
     <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
      <Field label={fr?"Œuvre originale":"Original source work"} value={daily.workTitle} onChange={v=>changeDaily("workTitle",v)}/>
      <Field label={fr?"Référence précise (livre, acte, passage)":"Exact reference (book, act, passage)"} value={daily.sourceReference} onChange={v=>changeDaily("sourceReference",v)}/>
      <Field label={fr?"Lien HTTPS vérifié vers l'œuvre":"Verified HTTPS link to work"} value={daily.sourceUrl} onChange={v=>changeDaily("sourceUrl",v)}/>
      <Field label={fr?"Crédit de la traduction française":"French translation credit"} value={daily.translationCredit} onChange={v=>changeDaily("translationCredit",v)}/>
     </div>
     <label style={fieldStyle}>{fr?"Statut des droits":"Rights basis"}
      <select value={daily.rightsBasis} onChange={e=>changeDaily("rightsBasis",e.target.value)} style={inputStyle}>
       <option value="public_domain">{fr?"Œuvre dans le domaine public":"Public domain"}</option>
       <option value="licensed">{fr?"Autorisation de reproduction obtenue":"Reproduction permission obtained"}</option>
      </select>
     </label>
    </>:null}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(270px,1fr))",gap:12}}>
     <Field large label={fr?"Réflexion NextScenes (anglais, facultatif)":"NextScenes interpretation (English, optional)"}
       value={daily.reflection} onChange={v=>changeDaily("reflection",v)}/>
     <Field large label={fr?"Réflexion NextScenes (français, facultatif)":"NextScenes interpretation (French, optional)"}
       value={daily.reflectionFr} onChange={v=>changeDaily("reflectionFr",v)}/>
    </div>
    <label style={{display:"flex",gap:9,alignItems:"start",fontSize:13,color:"#315846"}}>
     <input type="checkbox" checked={daily.rightsVerified===true} onChange={e=>changeDaily("rightsVerified",e.target.checked)}/>
     {daily.sourceType==="quotation"
       ?(fr?"Je confirme avoir vérifié l'attribution, la source et les droits de reproduction, y compris ceux de la traduction.":
          "I confirm the attribution, original source, reproduction rights and French translation rights were checked.")
       :(fr?"Je confirme que ces textes sont des créations originales approuvées.":
          "I confirm these are original, editorially approved words.")}
    </label>
   </div>:<div style={{display:"grid",gap:12}}>
    <label style={fieldStyle}>{fr?"Choisissez une énigme existante":"Choose an existing Mystery250 puzzle"}
     <select style={inputStyle} value={weekly.mysteryId} onChange={e=>selectMystery(e.target.value)}>
      <option value="">{fr?"Choisir une énigme…":"Select a puzzle…"}</option>
      {mysteries.map(m=><option key={m.id} value={m.id}>{m.title}</option>)}
     </select>
    </label>
    <p style={{margin:0,fontSize:12,color:"#526e60"}}>{fr?"La publication ne modifie pas la réponse ni la date de révélation de l'énigme.":
       "Scheduling changes only the homepage selection. Puzzle answers and reveal dates are never modified."}</p>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(270px,1fr))",gap:12}}>
     <Field label="Title (English)" value={weekly.title} onChange={v=>changeWeekly("title",v)}/>
     <Field label="Titre (français)" value={weekly.titleFr} onChange={v=>changeWeekly("titleFr",v)}/>
     <Field large label="Teaser (English)" value={weekly.teaser} onChange={v=>changeWeekly("teaser",v)}/>
     <Field large label="Présentation (français)" value={weekly.teaserFr} onChange={v=>changeWeekly("teaserFr",v)}/>
     <Field label="CTA (English, optional)" value={weekly.cta} onChange={v=>changeWeekly("cta",v)}/>
     <Field label="CTA (français, facultatif)" value={weekly.ctaFr} onChange={v=>changeWeekly("ctaFr",v)}/>
    </div>
   </div>}
   {isLiveApproved?<p style={{color:"#965c08",fontSize:13}}>
    {fr?"Cette entrée est publiée. Désactivez-la avant toute modification.":
       "This entry is currently eligible for publication. Pause it before editing."}</p>:null}
   <div style={{marginTop:15}}>
    <button type="button" disabled={busy||isLiveApproved||!dateKey} style={{...btn,background:"#116f43",color:"white",opacity:(busy||isLiveApproved)?0.65:1}}
      onClick={()=>void save()}>{busy?(fr?"En cours…":"Working…"):(fr?"Enregistrer le brouillon":"Save draft (not published)")}</button>
   </div>
  </section>
  <section className="ns-card" style={{padding:18}}>
   <h3 style={{margin:"0 0 9px",fontSize:18}}>{fr?"File de publication":"Publication queue"}</h3>
   <p style={{fontSize:13,color:"#526e60",margin:"0 0 13px"}}>{approvedFuture} {fr?"entrées approuvées à venir. Une date = une seule entrée.":
       "approved upcoming entries. Each date has one bilingual record."}</p>
   <div style={{display:"grid",gap:9}}>
    {list.length===0?<p style={{margin:0,color:"#5b7064",fontSize:14}}>{fr?"Aucune entrée enregistrée. Les anciennes Pensées quotidiennes restent visibles jusqu’à la prochaine publication approuvée.":
      "No scheduled entries yet. Previously published Daily Words remain visible until an approved entry is due."}</p>:null}
    {list.map(item=>{
      const live=item.status==="approved"&&item.dateKey<=utcToday();
      return <div key={String(item._id)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,
        flexWrap:"wrap",padding:12,borderRadius:12,border:"1px solid #d3e5d9",background:item.status==="approved"?"#f0f8f2":"#fff"}}>
       <div style={{minWidth:0}}>
        <strong style={{fontSize:14}}>{item.dateKey}</strong>
        <span style={{fontSize:12,color:"#537064",marginLeft:8}}>{item.status==="approved"?(live?(fr?"En vigueur":"Due / live"):fr?"Programmé":"Scheduled"):(fr?"Brouillon":"Draft")}</span>
        <p style={{fontSize:13,color:"#496b58",margin:"4px 0 0",overflowWrap:"anywhere"}}>
         {item.kind==="dailyWisdom"?(item.daily?.quote||"").slice(0,120):(item.weekly?.title||"Mystery250")}
        </p>
       </div>
       <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
        <button type="button" disabled={busy} style={btn} onClick={()=>choose(item)}>{fr?"Ouvrir":"Open"}</button>
        {item.status==="approved"?<button type="button" disabled={busy} style={btn} onClick={()=>void changeApproval(item,"pause")}>
          {fr?"Suspendre":"Pause"}
         </button>:<button type="button" disabled={busy} style={{...btn,background:"#e7f4ea"}} onClick={()=>void changeApproval(item,"approve")}>
          {fr?"Approuver":"Approve"}
         </button>}
       </div>
      </div>;
    })}
   </div>
  </section>
 </div>;
}
